import productionConfig from '../config/production';

/**
 * Production Error Logging Service
 * Handles error logging for production environments
 */

class ErrorLogger {
  constructor() {
    this.config = productionConfig;
    this.isProduction = this.config.isProduction;
    this.logLevel = this.isProduction ? 'error' : 'debug';
    this.logQueue = [];
    this.flushTimer = null;
  }

  /**
   * Log error with appropriate level
   * @param {Error|string} error - Error object or message
   * @param {Object} context - Additional context information
   * @param {string} level - Log level (error, warn, info, debug)
   */
  log(error, context = {}, level = 'error') {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : null,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // In production, send to logging service
    if (this.isProduction) {
      this.sendToLoggingService(logEntry);
    } else {
      // In development, use console
      this.logToConsole(logEntry);
    }
  }

  /**
   * Log error
   * @param {Error|string} error - Error object or message
   * @param {Object} context - Additional context information
   */
  error(error, context = {}) {
    this.log(error, context, 'error');
  }

  /**
   * Log warning
   * @param {string} message - Warning message
   * @param {Object} context - Additional context information
   */
  warn(message, context = {}) {
    this.log(message, context, 'warn');
  }

  /**
   * Log info
   * @param {string} message - Info message
   * @param {Object} context - Additional context information
   */
  info(message, context = {}) {
    this.log(message, context, 'info');
  }

  /**
   * Log debug information
   * @param {string} message - Debug message
   * @param {Object} context - Additional context information
   */
  debug(message, context = {}) {
    if (!this.isProduction) {
      this.log(message, context, 'debug');
    }
  }

  /**
   * Send log entry to external logging service
   * @param {Object} logEntry - Log entry object
   */
  sendToLoggingService(logEntry) {
    if (!this.config.errorLogging.enabled) {
      return;
    }

    // Add to queue for batch processing
    this.logQueue.push(logEntry);
    
    // Flush queue if it reaches batch size
    if (this.logQueue.length >= this.config.errorLogging.batchSize) {
      this.flushLogQueue();
    } else if (!this.flushTimer) {
      // Set up automatic flush
      this.flushTimer = setTimeout(() => {
        this.flushLogQueue();
      }, this.config.errorLogging.flushInterval);
    }
  }

  /**
   * Flush the log queue to the logging service
   */
  async flushLogQueue() {
    if (this.logQueue.length === 0) return;

    const logsToSend = [...this.logQueue];
    this.logQueue = [];
    
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    try {
      if (window.fetch) {
        const response = await fetch(this.config.errorLogging.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            logs: logsToSend,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } else {
        throw new Error('Fetch API not available');
      }
    } catch (error) {
      // Fallback to localStorage if network fails
      logsToSend.forEach(log => this.storeLocally(log));
    }
  }

  /**
   * Store log entry locally as fallback
   * @param {Object} logEntry - Log entry object
   */
  storeLocally(logEntry) {
    try {
      const logs = JSON.parse(localStorage.getItem('errorLogs') || '[]');
      logs.push(logEntry);
      
      // Keep only last 100 entries
      if (logs.length > 100) {
        logs.splice(0, logs.length - 100);
      }
      
      localStorage.setItem('errorLogs', JSON.stringify(logs));
    } catch (error) {
      // If localStorage fails, silently ignore
    }
  }

  /**
   * Log to console in development
   * @param {Object} logEntry - Log entry object
   */
  logToConsole(logEntry) {
    const { level, message, stack, context } = logEntry;
    
    switch (level) {
      case 'error':
        console.error(`[${logEntry.timestamp}] ${message}`, { stack, context });
        break;
      case 'warn':
        console.warn(`[${logEntry.timestamp}] ${message}`, { context });
        break;
      case 'info':
        console.info(`[${logEntry.timestamp}] ${message}`, { context });
        break;
      case 'debug':
        console.debug(`[${logEntry.timestamp}] ${message}`, { context });
        break;
      default:
        console.log(`[${logEntry.timestamp}] ${message}`, { context });
    }
  }

  /**
   * Get stored error logs
   * @returns {Array} Array of stored log entries
   */
  getStoredLogs() {
    try {
      return JSON.parse(localStorage.getItem('errorLogs') || '[]');
    } catch (error) {
      return [];
    }
  }

  /**
   * Clear stored error logs
   */
  clearStoredLogs() {
    try {
      localStorage.removeItem('errorLogs');
    } catch (error) {
      // Silently ignore
    }
  }
}

// Create and export singleton instance
const errorLogger = new ErrorLogger();
export default errorLogger;
